import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { Lookup } from '@models/lookup.model';
import { LookupService } from '@services/lookup.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash-es';
import { AdjustmentModalTemplateComponent } from '../adjustment-modal-template/adjustment-modal-template.component';
import { Adjustment, AdjustmentsTypeRateVariants } from '../adjustment.model';
import { takeUntil } from 'rxjs/operators';
import { AdjustmentsService } from '../adjustments.service';

export const INVENTORY_ADJ_ID = 327;
export const RECEIVABLE_ADJ_ID = 328;
export const PAYABLE_ADJ_ID = 329;

export const INVENTORY_DEFAULT_IR_VARIANT = 356;
export const RECEIVABLE_DEFAULT_IR_VARIANT = 358;
export const PAYABLE_DEFAULT_IR_VARIANT = 358;

@Component({
  selector: 'app-adjustment-selector',
  templateUrl: './adjustment-selector.component.html',
  styleUrls: ['./adjustment-selector.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AdjustmentSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(AdjustmentModalTemplateComponent, { static: false }) modal: AdjustmentModalTemplateComponent;
  @Input() readOnly = false;
  @Input() adjustment: Adjustment;
  @Input() interestRateDetailsProvided: boolean;
  invetoryAdjustmentId = INVENTORY_ADJ_ID;
  @Input() set selectedAdjustment(value: Adjustment) {
    if (value.adjustmentsTestedPartyId) {
      this.adjustment = value;
    }
  }

  @Input() adjustmentTypes: Lookup[];
  @Input() interestRateVariants: Lookup[];

  interestRateVariants_Local: Lookup[];
  interestRateVariants_NoHoldingPeriod: Lookup[];
  adjustmentForm: UntypedFormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private lookupService: LookupService, private fb: UntypedFormBuilder, private adjustmentService: AdjustmentsService) {
    this.adjustmentForm = this.fb.group(
      {
        adjustmentTypes: [[], [Validators.required]],
        interestRateVariants_327: this.getDefaultRateVariant(INVENTORY_ADJ_ID),
        interestRateVariants_328: this.getDefaultRateVariant(RECEIVABLE_ADJ_ID),
        interestRateVariants_329: this.getDefaultRateVariant(PAYABLE_ADJ_ID),
      },

      { updateOn: 'change' }
    );
  }

  ngOnInit() {
    this.interestRateVariants_Local = this.interestRateVariants.map((l) => {
      return { ...l };
    });
    this.interestRateVariants_NoHoldingPeriod = this.interestRateVariants
      .filter((l) => l.lookupId !== PAYABLE_DEFAULT_IR_VARIANT)
      .map((l) => {
        return { ...l };
      });

    this.markDefaultInterestRateVariant(this.interestRateVariants_Local, PAYABLE_DEFAULT_IR_VARIANT);
    this.markDefaultInterestRateVariant(this.interestRateVariants_NoHoldingPeriod, INVENTORY_DEFAULT_IR_VARIANT);

    this.adjustmentService
      .getByTestedPartyId(this.adjustment.adjustmentsTestedPartyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_adjustment) => {
        if (_adjustment) {
          this.adjustment = _adjustment;
        }
        this.mapAdjustmentTypeVariantLookups();

        if (this.adjustment.adjustmentsTypeRateVariants) {
          this.adjustmentForm.patchValue({
            adjustmentTypes: this.adjustment.adjustmentsTypeRateVariants.map((atv) => atv.adjustmentTypesLookup),
            interestRateVariants_327: this.getAdjustmentsTypeRateVariant(INVENTORY_ADJ_ID),
            interestRateVariants_328: this.getAdjustmentsTypeRateVariant(RECEIVABLE_ADJ_ID),
            interestRateVariants_329: this.getAdjustmentsTypeRateVariant(PAYABLE_ADJ_ID),
          });
        } else {
          this.adjustmentForm.patchValue({
            interestRateVariants_327: this.getDefaultRateVariant(INVENTORY_ADJ_ID),
            interestRateVariants_328: this.getDefaultRateVariant(RECEIVABLE_ADJ_ID),
            interestRateVariants_329: this.getDefaultRateVariant(PAYABLE_ADJ_ID),
          });
        }
      });
  }

  markDefaultInterestRateVariant(variants: Lookup[], defaultVariant: number) {
    variants.find((a) => a.lookupId === defaultVariant).displayText += ' - default selection';
  }

  getDefaultRateVariant(adjTypeId: number): Lookup {
    switch (adjTypeId) {
      case RECEIVABLE_ADJ_ID:
        return this.getLookupById(RECEIVABLE_DEFAULT_IR_VARIANT, this.interestRateVariants_Local);
      case PAYABLE_ADJ_ID:
        return this.getLookupById(RECEIVABLE_DEFAULT_IR_VARIANT, this.interestRateVariants_Local);
      default:
        return this.getLookupById(INVENTORY_DEFAULT_IR_VARIANT, this.interestRateVariants_NoHoldingPeriod);
    }
  }

  getAdjustmentsTypeRateVariant(adjTypeId: number) {
    const ad = this.adjustment.adjustmentsTypeRateVariants.find((t) => t.adjustmentTypeId === adjTypeId);
    console.log(adjTypeId);
    if (ad !== undefined) {
      return ad.interestRateVariantsLookup;
    }
    // default
    return this.getDefaultRateVariant(adjTypeId);
  }

  onSubmit() {
    if (this.adjustmentForm.valid) {
      /*
      this.adjustment.adjustmentTypesLookup = this.adjustmentForm.value.adjustmentTypes;
      this.adjustment.interestRateVariantsLookup = this.adjustmentForm.value.interestRateVariants;
      this.adjustment.adjustmentType = this.getCommaDelimitedValues(this.adjustment.adjustmentTypesLookup.map((t) => t.lookupId));
      this.adjustment.interestRateVariantId = this.adjustmentForm.value.interestRateVariants.lookupId;
      */
      const adjustmentTypes = this.adjustmentForm.value.adjustmentTypes;

      this.adjustment.adjustmentsTypeRateVariants = adjustmentTypes.map((a) =>
        this.mapToAdjustmentsTypeRateVariants(
          this.adjustment.adjustmentId,
          a,
          this.adjustmentForm.controls['interestRateVariants_' + a.lookupId].value
        )
      );

      this.close(this.adjustment);
    }
  }

  mapToAdjustmentsTypeRateVariants(adjId: number, adjType: Lookup, interestRateVariantsLookup: Lookup): AdjustmentsTypeRateVariants {
    return {
      adjustmentId: adjId,
      adjustmentTypeId: adjType.lookupId,
      adjustmentTypeRateVariantId: 0,
      interestRateVariantId: interestRateVariantsLookup ? interestRateVariantsLookup.lookupId : 0,
    };
  }

  close(adjustment: Adjustment = null) {
    this.modal.close(adjustment);
  }

  getCommaDelimitedValues(ids: number[]) {
    return _.toString(ids);
  }

  mapAdjustmentTypeVariantLookups() {
    if (this.adjustment) {
      this.adjustment.adjustmentsTypeRateVariants = this.adjustment.adjustmentsTypeRateVariants
        ? this.adjustment.adjustmentsTypeRateVariants.map((atv) => {
            atv.adjustmentTypesLookup = this.getLookupById(+atv.adjustmentTypeId, this.adjustmentTypes);
            atv.interestRateVariantsLookup = this.getLookupById(
              +atv.interestRateVariantId,
              atv.adjustmentTypeId === INVENTORY_ADJ_ID ? this.interestRateVariants_NoHoldingPeriod : this.interestRateVariants_Local
            );

            return atv;
          })
        : null;
    }
  }

  isReadOnly(selectedAdjTypes: Lookup[], adjTypeLookupId) {
    return selectedAdjTypes == null ? true : selectedAdjTypes.find((a) => a.lookupId === adjTypeLookupId) === undefined;
  }

  getLookupById(id: number, lookup: Lookup[]) {
    return lookup
      ? lookup.find((l) => {
          return l.lookupId === id;
        })
      : null;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
